// Shared derivations over the battlePassEvent shape (see api.js for the contract).

// Convert the prizes/claimablePrizes maps into a sorted milestone array.
// Single source of truth for prize-key parsing and claim-state flags.
export function milestonesFromEvent(event) {
    return Object.keys(event.prizes)
        .map((key) => ({
            id: parseInt(key.replace('prize', '')),
            key,
            pointsNeeded: event.prizes[key].pointsNeeded,
            items: event.prizes[key].items,
            seasonPass: event.prizes[key].seasonPass,
            reached: event.points >= event.prizes[key].pointsNeeded,
            claimed: event.claimablePrizes[key]?.claimed === 1,
            seasonPassClaimed: event.claimablePrizes[key]?.seasonPassClaimed === 1,
        }))
        .sort((a, b) => a.id - b.id)
        .map((m, i, all) => ({
            ...m,
            // "current": first unreached milestone (or none when all reached)
            isCurrent: !m.reached && (i === 0 || all[i - 1].reached),
        }));
}

// Format an amount in the shop's active currency (selectedCurrency, which is
// driven by the packs' paymentHub price currency — same as StorePack does).
// fractionDigits null → currency default (2). Falls back to USD before the
// first pack sets the currency.
export function formatCurrency(amount, currency, fractionDigits = null) {
    const opts = { style: 'currency', currency: currency || 'USD' };
    if (fractionDigits !== null) {
        opts.minimumFractionDigits = fractionDigits;
        opts.maximumFractionDigits = fractionDigits;
    }
    return new Intl.NumberFormat(undefined, opts).format(amount);
}

// Find the webshop-only pack that carries the seasonPass perk — matched by the
// event's configured pack id, with the perk as a fallback. It's delivered in
// get-packs like any hidden pack and carries the player's localized price.
export function findSeasonPassPack(packs, event) {
    return (packs || []).find(
        (pack) => pack.internalId === event.seasonPassPackId || pack.perks?.includes('seasonPass')
    ) ?? null;
}

// Premium price taken from the actual pack's localized paymentHub price (the
// real amount the player will be charged, in their own currency). Falls back to
// the config premiumPriceCents — a USD value — before the pack is in get-packs.
export function premiumPriceLabel(event, pack) {
    const price = pack?.paymentHub?.price;
    if (price && price.amount !== 'missing' && price.currency !== 'missing') {
        return formatCurrency(price.amount / 100, price.currency);
    }
    return formatCurrency((event.premiumPriceCents ?? 0) / 100, 'USD');
}

// Earn rate amount shown in the currency PaymentHub actually charges. Points
// accrue on the pack's BASE USD price (Payment.js `spendOnWebshop` = points per
// $1, NOT the localized charge), so we display what that $1 of base value is
// worth in the player's currency. Rate = the season pass pack's localized
// PaymentHub price over its USD price. We use `paymentHub.price` /
// `webshopPrices.USD.price` — both ALWAYS present and both post-discount, so the
// discount cancels and the ratio is a pure FX rate. We deliberately DON'T use
// the `*.fullPrice` fields: the web-shop proxy only emits them when the pack
// carries a discount (store.controller.ts sets paymentHub.fullPrice only when
// pack.fullSku exists, which the backend only sets on discounted packs), so a
// no-discount season pass pack has no fullPrice. `paymentHub.price.currency` is
// the region currency PaymentHub picks from the user's IP. Falls back to plain
// $1 if the localized price is unavailable. The "{amount} = {points} points"
// wording is localised in the component via i18n.
export function earnRateAmount(pack) {
    const local = pack?.paymentHub?.price;
    const usdCents = pack?.webshopPrices?.USD?.price;
    if (
        local &&
        local.amount !== 'missing' &&
        local.currency !== 'missing' &&
        usdCents > 0
    ) {
        return formatCurrency(local.amount / usdCents, local.currency);
    }
    return formatCurrency(1, 'USD');
}

// Whether a claim outcome's `error` deserves a report from the component that made the
// claim. api.js already reports transport-level outcomes itself — a non-2xx at error
// level and an expired session at info — so repeating those here produced two Rollbar
// items per failed click (#2535 + #2463 for a single daily-bonus 500). Business
// rejections the server answers 200 with (e.g. 'onCooldown' while the client thought
// the button was available) are the disagreement this report exists to surface.
export function shouldReportClaimError(error) {
    if (!error) return false;
    return error !== 'requestFailed' && error !== 'sessionExpired';
}
